import { DownloadOutlined } from "@ant-design/icons";
import { Alert, App, Button, Card, Col, Row, Select, Space, Typography } from "antd";
import { useEffect, useState } from "react";

import { useExport, type ExportKind } from "../../api/exports";
import { useNames } from "../../api/names";
import type { Group } from "../../api/types";

interface ExportCard {
  kind: ExportKind;
  title: string;
  description: string;
  body?: Record<string, unknown>;
  needsGroup: boolean;
  usesFilters: boolean;
  fallbackName: string;
}

export function ExportsPage() {
  const { catalog } = useNames();
  const { message } = App.useApp();
  const run = useExport();
  const [group, setGroup] = useState<Group>();
  const [regionId, setRegionId] = useState<number>();
  const [siteId, setSiteId] = useState<number>();
  const [busy, setBusy] = useState<string>();
  const groups = catalog.data?.groups ?? [];
  const regions = catalog.data?.regions ?? [];
  const sites = (catalog.data?.sites ?? []).filter((site) => regionId === undefined || site.regionId === regionId);

  useEffect(() => {
    if (group === undefined && groups.length > 0) setGroup(groups[0].id);
  }, [group, groups]);

  const cards: ExportCard[] = [
    { kind: "scores", title: "成绩", description: "每位考生的总分、已作答题数、组内与考点内排名。", needsGroup: true, usesFilters: true, fallbackName: "scores.csv" },
    { kind: "answers", title: "答案", description: "每位考生每题的当前答案，一行一人、一列一题。", needsGroup: true, usesFilters: true, fallbackName: "answers.csv" },
    { kind: "attendance", title: "参加人数", description: "各考点的考生数、已登录、实际参加（已提交）和已关闭账号数。", needsGroup: false, usesFilters: false, body: {}, fallbackName: "attendance.csv" },
    { kind: "answer-sheets", title: "答卷（ZIP）", description: "所选范围内每位考生一份可打印的 HTML 答卷。范围过大时请按区域或考点分批。", needsGroup: true, usesFilters: true, fallbackName: "answer-sheets.zip" },
    { kind: "paper", title: "考题（无答案）", description: "按源码顺序的可打印试卷，浏览器打印为 PDF，Word 可直接打开。", needsGroup: true, usesFilters: false, body: { withAnswers: false }, fallbackName: "paper.html" },
    { kind: "paper", title: "考题（含标准答案）", description: "同上，每题附正确答案与分值。", needsGroup: true, usesFilters: false, body: { withAnswers: true }, fallbackName: "paper-with-answers.html" }
  ];

  const download = async (card: ExportCard) => {
    const key = `${card.kind}-${card.title}`;
    setBusy(key);
    try {
      const body = {
        ...(card.needsGroup ? { group } : {}),
        ...(card.kind === "attendance" && group ? { group } : {}),
        ...(card.usesFilters ? { regionId, siteId } : {}),
        ...card.body
      };
      const filename = await run.mutateAsync({ kind: card.kind, body, fallbackName: card.fallbackName });
      message.success(`已生成 ${filename}`);
    } catch (caught) {
      message.error(caught instanceof Error ? caught.message : "导出失败");
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space wrap>
        <Select<Group> style={{ width: 140 }} value={group} onChange={setGroup}
          options={groups.map((item) => ({ value: item.id, label: item.name }))} />
        <Select<number> allowClear showSearch optionFilterProp="label" placeholder="全部考点区域" style={{ width: 160 }} value={regionId}
          onChange={(value) => { setRegionId(value); setSiteId(undefined); }}
          options={regions.map((item) => ({ value: item.id, label: item.name }))} />
        <Select<number> allowClear showSearch optionFilterProp="label" placeholder="全部考点" style={{ width: 220 }} value={siteId}
          onChange={setSiteId}
          options={sites.map((item) => ({ value: item.id, label: `${item.name}（${item.id}）` }))} />
        <Typography.Text type="secondary">成绩和答案按当前答案即时计算，文件由服务端同步生成后直接下载。</Typography.Text>
      </Space>
      {groups.length === 0 && catalog.isSuccess && <Alert type="warning" showIcon message="当前账号没有可导出的组别。" />}
      <Row gutter={[16, 16]}>
        {cards.map((card) => (
          <Col key={`${card.kind}-${card.title}`} xs={24} md={12} lg={8}>
            <Card
              title={card.title}
              actions={[
                <Button
                  key="download"
                  type="link"
                  icon={<DownloadOutlined />}
                  loading={busy === `${card.kind}-${card.title}`}
                  disabled={(card.needsGroup && !group) || (busy !== undefined && busy !== `${card.kind}-${card.title}`)}
                  onClick={() => void download(card)}
                >
                  下载
                </Button>
              ]}
            >
              <Typography.Paragraph type="secondary" style={{ minHeight: 44, marginBottom: 0 }}>{card.description}</Typography.Paragraph>
            </Card>
          </Col>
        ))}
      </Row>
    </Space>
  );
}
